import jwt from 'jwt-decode';
import PubSub from 'pubsub-js'

import fetcher from '../services/fetcher';
import FirebasePasswordAuth from './firebasePasswordAuth';
import { isPrerendering } from './prerender';

const invalidStoredTokens = new Set(['', 'undefined', 'null']);
const isFirebasePasswordAuthEnabled = process.env.REACT_APP_FIREBASE_PASSWORD_AUTH === 'true';
const isFirebaseUser = user => Boolean(user && user.authProvider === 'firebase_password');
const invalidFirebaseSessionCodes = new Set([
  'FIREBASE_SESSION_INVALID',
  'auth/no-current-user',
  'auth/user-disabled',
  'auth/user-token-expired'
]);
let sessionInitializationPromise;
let sessionInitializationComplete = false;

const removeStoredToken = () => {
  try {
    localStorage.removeItem('token');
  } catch (err) {
    console.warn('[auth] unable to remove invalid token');
  }
};

const getStoredToken = () => {
  try {
    return localStorage.getItem('token');
  } catch (err) {
    console.warn('[auth] local storage is unavailable');
    return null;
  }
};

const storeToken = token => {
  try {
    localStorage.setItem('token', token);
  } catch (err) {
    throw new Error('Unable to store authentication token.');
  }
};

const decodeToken = token => {
  if (typeof token !== 'string' || invalidStoredTokens.has(token.trim())) return null;

  try {
    const user = jwt(token);

    if (!user || typeof user !== 'object' || typeof user.email !== 'string' || !user.email.trim()) {
      return null;
    }

    if (user.exp != null && (!Number.isFinite(Number(user.exp)) || Number(user.exp) <= Math.floor(Date.now() / 1000))) {
      return null;
    }

    return user;
  } catch (err) {
    return null;
  }
};

const Auth = {
  requiresSessionInitialization: () => {
    if (isPrerendering()) return false;
    const currentUser = Auth.getUser();
    return isFirebasePasswordAuthEnabled
      && !sessionInitializationComplete
      && (!currentUser || isFirebaseUser(currentUser));
  },
  getUser: () => {
    if (isPrerendering()) {
      removeStoredToken();
      return null;
    }

    const token = getStoredToken();
    if (!token) return null;

    const user = decodeToken(token);
    if (!user) {
      console.warn('[auth] malformed or expired token removed');
      removeStoredToken();
    }

    return user;
  },
  signIn: async (name, email) => {
    const data = await fetcher.signInUser(name, email);
    const token = data && data.token;
    const user = decodeToken(token);

    if (!user) {
      throw new Error('The sign-in response did not contain a valid authentication token.');
    }

    storeToken(token);
    PubSub.publish('LOGIN', 'signin');
    return user;
  },
  signInWithFirebase: async idToken => {
    const data = await fetcher.exchangeFirebaseSession(idToken);
    const token = data && data.token;
    const user = decodeToken(token);

    if (!user) {
      const err = new Error((data && data.error) || 'Unable to complete Firebase sign-in.');
      err.code = data && data.code;
      throw err;
    }

    storeToken(token);
    sessionInitializationComplete = true;
    PubSub.publish('LOGIN', 'firebase-signin');
    return user;
  },
  isLogged: () => {
    const user = Auth.getUser()
    return (user != null);
  },
  refreshUser: async () => {
    const currentUser = Auth.getUser();
    if (!currentUser) return null;

    if (isFirebaseUser(currentUser)) {
      return Auth.refreshFirebaseUser(currentUser);
    }

    try {
      const data = await fetcher.refreshToken(currentUser.name, currentUser.email);
      const token = data && data.token;
      const refreshedUser = decodeToken(token);

      if (!refreshedUser) {
        console.warn('[auth] refresh returned an invalid token; existing session preserved');
        return currentUser;
      }

      storeToken(token);
      PubSub.publish('REFRESH', 'refreshed');
      return refreshedUser;
    } catch (err) {
      console.warn('[auth] refresh failed; existing session preserved');
      return currentUser;
    }
  },
  refreshFirebaseUser: async currentUser => {
    try {
      const firebaseUser = await FirebasePasswordAuth.reloadCurrentUser();
      if (!firebaseUser.emailVerified) {
        await Auth.logOut();
        return null;
      }
      const idToken = await FirebasePasswordAuth.getIdToken(true);
      return await Auth.signInWithFirebase(idToken);
    } catch (err) {
      if (invalidFirebaseSessionCodes.has(err && err.code)) {
        await Auth.logOut();
        return null;
      }
      console.warn('[auth] Firebase refresh failed; existing GoldAPI session preserved');
      return currentUser;
    }
  },
  initializeSession: () => {
    if (isPrerendering()) {
      removeStoredToken();
      sessionInitializationComplete = true;
      return Promise.resolve(null);
    }

    if (!isFirebasePasswordAuthEnabled) {
      return Promise.resolve(Auth.getUser());
    }
    if (sessionInitializationComplete) return Promise.resolve(Auth.getUser());
    if (sessionInitializationPromise) return sessionInitializationPromise;

    const initializationPromise = (async () => {
      const currentUser = Auth.getUser();
      if (currentUser && !isFirebaseUser(currentUser)) return currentUser;

      try {
        const firebaseUser = await FirebasePasswordAuth.waitForAuthState();

        if (!firebaseUser) {
          if (isFirebaseUser(currentUser)) {
            removeStoredToken();
            PubSub.publish('LOGOUT', 'firebase-session-missing');
          }
          return Auth.getUser();
        }

        if (!firebaseUser.emailVerified) {
          if (isFirebaseUser(currentUser)) removeStoredToken();
          return null;
        }

        const idToken = await firebaseUser.getIdToken(true);
        return await Auth.signInWithFirebase(idToken);
      } catch (err) {
        if (isFirebaseUser(currentUser) && invalidFirebaseSessionCodes.has(err && err.code)) {
          removeStoredToken();
          try {
            await FirebasePasswordAuth.logOut();
          } catch (logoutErr) {
            // Local GoldAPI session is already invalidated.
          }
          PubSub.publish('LOGOUT', 'firebase-session-invalid');
          return null;
        }
        console.warn('[auth] Firebase session restore failed; existing session preserved');
        return currentUser;
      }
    })();
    sessionInitializationPromise = initializationPromise;
    initializationPromise.then(
      () => {
        sessionInitializationComplete = true;
        if (sessionInitializationPromise === initializationPromise) sessionInitializationPromise = null;
      },
      () => {
        if (sessionInitializationPromise === initializationPromise) sessionInitializationPromise = null;
      }
    );

    return initializationPromise;
  },
  logOut: async () => {
    const currentUser = Auth.getUser();
    sessionInitializationPromise = null;
    sessionInitializationComplete = false;
    if (isFirebaseUser(currentUser) && isFirebasePasswordAuthEnabled) {
      try {
        await FirebasePasswordAuth.logOut();
      } catch (err) {
        console.warn('[auth] unable to clear Firebase session');
      }
    }
    removeStoredToken();
    PubSub.publish('LOGOUT', 'signout');
  },
  unsubscribe: async (email) => {
    const result = await fetcher.unsubscribe(email);
    PubSub.publish('UNSUB', 'unsubscribed');
    return result;
  },
  revokeApiKey: async () => {
    const result = await fetcher.revokeApiKey();
    if (result && result.apiKey) {
      await Auth.refreshUser();
      return Auth.getUser();
    }
    return result;
  }
}

export default Auth;
